import type { DateTime } from 'luxon';
import { showsTermState, termLabel, termRange, termState, termZoneNote, type PolicySummary } from '@/lib/policy';
import { formatMoney as formatMoneyJs } from '@/utils/formatMoney';
import { getTotalAllInCostValue } from '@/utils/getTotalAllInCostValue';

/* The rows of the Policy Details cards. Every card value is worked out and formatted here, and only
   here: the cards render the rows they are given. Money figures read exactly as today's policy page
   shows them, and the only figures computed in the browser are the two today's page computes
   (all-in cost and the rate fallback), computed the same way. */

/**
 * One row of a card: what it is, the text it shows, whether that text is a money figure or a rate (set in
 * a monospace face), and an optional note shown as the value's tooltip (the zone note on a term date).
 */
export type Figure = { label: string; text: string; numeric: boolean; note?: string };

const formatMoney = formatMoneyJs as (value: number, options: { prefix: string; withDecimals: boolean }) => string;

/** What a card shows for a figure the response doesn't have. */
const MISSING = '—';

const text = (label: string, value: string): Figure => ({ label, text: value, numeric: false });

/** A money figure as today's stat band shows it ("$ 12,000.00", or "$0" for zero); "—" when missing, never "$0". */
const money = (label: string, value: number | null | undefined): Figure =>
  value == null ? text(label, MISSING) : { label, text: formatMoney(value, { prefix: '$', withDecimals: true }), numeric: true };

/** All-in cost needs both fees and locations: without fees the calculation throws, and without locations it counts the premiums as $0. */
const allInCost = (policy: PolicySummary): Figure =>
  policy.fees == null || policy.locations == null ? text('All-in cost', MISSING) : money('All-in cost', getTotalAllInCostValue(policy));

/** The quoted rate when set, else GWP at binding per $100 of insured value to four places, else N/A: today's page's rule. */
const rate = ({ quoteRate, netPremium, coveredValue }: PolicySummary): Figure => {
  if (quoteRate) return { label: 'Rate', text: `$ ${quoteRate}`, numeric: true };
  if (netPremium != null && coveredValue) return { label: 'Rate', text: `$ ${((netPremium / coveredValue) * 100).toFixed(4)}`, numeric: true };
  return text('Rate', 'N/A');
};

/** The Days left row: the term state, with the zone note when it shows a date; no row on an Inactive policy. */
const daysLeft = (policy: PolicySummary, now: DateTime, note: string): Figure[] => {
  if (!showsTermState(policy, now)) return [];
  const state = termState(policy, now);
  const row = text('Days left', termLabel(state));
  return [state.kind === 'running' ? row : { ...row, note }];
};

/** The "Term and premium" card's rows: Product, Term, Days left (left out on an Inactive policy), GWP at binding, All-in cost, Rate. */
export function termAndPremiumFigures(policy: PolicySummary, now: DateTime): Figure[] {
  const note = termZoneNote(policy);
  return [
    text('Product', policy.product?.name || MISSING),
    { ...text('Term', termRange(policy)), note },
    ...daysLeft(policy, now, note),
    money('GWP at binding', policy.netPremium),
    allInCost(policy),
    rate(policy),
  ];
}

/** The "Hail per occurrence" card's one row on a per-occurrence policy (perOccurrenceDeductible set, $0 included); none otherwise. */
export function hailPerOccurrenceFigures(policy: PolicySummary): Figure[] {
  return policy.perOccurrenceDeductible == null ? [] : [money('Hail agg. deductible', policy.perOccurrenceDeductible)];
}
